import React from 'react';
import PlayerFactionIcon from '../../PlayerFactionIcon/PlayerFactionIcon';
import HourglassIcon from '../../icons/HourglassIcon/HourglassIcon';
import {COMPLETE_BUTTON_MODES} from '../../../utils/gameUi/getCompleteButtonView';
import {getPlayerColorStyle} from '../../../utils/gameUi/getPlayerColorStyle';
import {GAME_UI_DARK_INK, getReadableInkColor} from '../../../utils/colors/getReadableInkColor';
import {joinClassNames} from '../../gameUi/joinClassNames';
import {
  CompleteButtonCheck,
  CompleteButtonCounter,
  CompleteButtonMaskIcon,
  CompleteButtonTimerRing,
  getCompleteButtonLabel
} from './CompleteButtonParts';
import './CurrentPlayerSection.css';

const renderTop = ({view, displayedPlayer, onPortraitClick}) => {
  if (view.mode === COMPLETE_BUTTON_MODES.WAITING) {
    return displayedPlayer ? (
      <span className={'gui-player-frame complete-phase-button_frame'}>
        <PlayerFactionIcon
          player={displayedPlayer}
          skinId={displayedPlayer?.selectedSkin}
          className={'complete-phase-button_icon'}
          onClick={onPortraitClick}
        />
      </span>
    ) : null;
  }
  if (view.mode === COMPLETE_BUTTON_MODES.LOCKED) {
    return view.stageIcon
      ? <CompleteButtonMaskIcon className={'complete-phase-button_plate-stage-icon'} src={view.stageIcon}/>
      : null;
  }
  return view.check ? <CompleteButtonCheck kind={view.check}/> : null;
};

const renderBottom = ({view}) => {
  if (view.mode === COMPLETE_BUTTON_MODES.WAITING || view.mode === COMPLETE_BUTTON_MODES.LOCKED) {
    return (
      <span className={'complete-phase-button_ok'}>
        <HourglassIcon
          className={'complete-phase-button_icon'}
          size={30}
          label={view.mode === COMPLETE_BUTTON_MODES.LOCKED ? 'Время вышло' : 'Ожидаем'}
        />
      </span>
    );
  }
  if (view.isCounterShown) {
    return <CompleteButtonCounter view={view}/>;
  }
  if (view.wellLabel) {
    return (
      <span className={'complete-phase-button_well'}>
        <span className={'complete-phase-button_well-label'}>{view.wellLabel}</span>
      </span>
    );
  }
  return null;
};

/**
 * Плита кнопки хода «А · Плита» без логики партии: что показать, решает
 * getCompleteButtonView, цвет — getCompleteButtonColor. Ее рисуют кнопка
 * хода матча (CompletePhaseButton) и витрина дизайн-системы в админке.
 *
 * Сверху галочка, портрет ожидаемого игрока или иконка этапа (время вышло),
 * снизу счетчик дел, подпись этапа или часы. Жетон этапа и свечение — за
 * краем плиты, снаружи кнопки: их обрезал бы clip-path плиты.
 *
 * Инлайн backgroundColor остается: поверх него CSS рисует рамку-градиент из
 * --gui-pc, а сам цвет — то, по чему кнопку узнают тесты и обучение
 */
const CompletePhaseButtonPlate = ({
  view,
  buttonColor,
  displayedPlayer = null,
  timerText = null,
  isTimerShown = false,
  timeLeftFraction = 1,
  isHurry = false,
  isContentShown = true,
  pulse = null,
  isFieldInset = false,
  disabled = false,
  cursor = 'pointer',
  isTutorialWaiting = false,
  onClick,
  onPortraitClick,
  title,
}) => {
  const buttonInk = buttonColor ? getReadableInkColor(buttonColor) : null;

  return (
    <div className="complete-phase-button-wrapper" data-field-inset={isFieldInset ? 'right' : undefined}>
      {timerText}
      <span className={'complete-phase-button_plate-wrap'}>
        {view.isGlowing && isContentShown && (
          // Ключ — этап и режим: «готово» следующего этапа монтирует ореол
          // заново, и задержка перед первой вспышкой отсчитывается снова
          <span key={`${view.stageIcon}|${view.mode}`} className={'complete-phase-button_halo'} aria-hidden={true}>
            <span/>
          </span>
        )}
        {view.isStageBadgeShown && isContentShown && view.stageIcon && (
          <span className={'complete-phase-button_stage-badge'} aria-hidden={true}>
            <span><CompleteButtonMaskIcon src={view.stageIcon}/></span>
          </span>
        )}
        <button
          type={'button'}
          onClick={onClick}
          disabled={disabled}
          className={joinClassNames(
            'gui-plate gui-plate_mine complete-phase-button',
            `complete-phase-button_mode_${view.mode}`,
            isHurry && 'complete-phase-button_hurry',
            pulse && `complete-phase-button_pulse_${pulse}`,
          )}
          data-gui-ink={buttonInk ? (buttonInk === GAME_UI_DARK_INK ? 'dark' : 'light') : undefined}
          data-mode={view.mode}
          data-tutorial-waiting={isTutorialWaiting ? 'true' : undefined}
          aria-label={getCompleteButtonLabel(view)}
          title={title}
          style={{
            ...getPlayerColorStyle(buttonColor),
            backgroundColor: buttonColor,
            cursor,
          }}
        >
          {isContentShown && (
            <>
              {isTimerShown && <CompleteButtonTimerRing fraction={timeLeftFraction}/>}
              <span className={'complete-phase-button_top'}>
                {renderTop({view, displayedPlayer, onPortraitClick})}
              </span>
              {renderBottom({view})}
            </>
          )}
        </button>
      </span>
    </div>
  );
};

export default CompletePhaseButtonPlate;
